import {
  ListRequest,
  useVbsOrderListQuery,
} from "@/services/backend/vbs/orders/api/order-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import { Search } from "lucide-react";
import { Order } from "@/services/backend/vbs/orders/types/order";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";

export function OrderTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (orderId: string, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const { data: list, isLoading, error } = useVbsOrderListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">
                {t("Eingang")}
              </TableHead>
              <TableHead className="whitespace-nowrap">
                {t("Zu Angebot")}
              </TableHead>
              <TableHead className="whitespace-nowrap">{t("Kanal")}</TableHead>
              <TableHead className="w-full whitespace-nowrap">
                {t("Kundenreferenz")}
              </TableHead>
              <TableHead className="w-48">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={5} />}
            {error && <TableRtkErrorRow error={error} colSpan={5} />}
            {emptyList && (
              <TableEmptyRow
                colSpan={5}
                text={t("Keine Auftragseingänge gefunden")}
              />
            )}
            {list &&
              list.items.map((order) => (
                <OrderTableRow
                  key={order.id}
                  order={order}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function OrderTableRow({
  order,
  renderRowLink,
}: {
  order: Order;
  renderRowLink: (orderId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">
        {new Date(order.date).toLocaleString()}
      </TableCell>
      <TableCell>
        {order.offerId === "00000000-0000-0000-0000-000000000000" ? (
          <span>{t("Ohne Angebot")}</span>
        ) : (
          <DocumentNumberById documentId={order.offerId} />
        )}
      </TableCell>
      <TableCell>{t(order.channel)}</TableCell>
      <TableCell>{t(order.customerReference)}</TableCell>
      <TableCell>
        <div className="flex space-x-2">
          {renderRowLink(
            order.id,
            <Button variant="ghost" className="-mt-2 -mb-4">
              <Search className="h-4 w-4" />
            </Button>,
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function OrderTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <OrderBySelect<string>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Eingang"), value: "date" },
          { label: t("Kundenreferenz"), value: "customerReference" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}

export function useDefaultVbsOrderListRequest(processId: string): ListRequest {
  return {
    process: { active: true, values: [processId] },
    customer: { active: false, values: [] },
    orderBy: [{ field: "date" }],
    limit: 50,
    offset: 0,
  };
}
